import NumberInput from '../../../components/NumberInput'
import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar, Plus, Loader2, ChevronDown, ChevronUp } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { useUnits } from '../../../contexts/UnitsContext'
import { isPlausibleWeightKg, MAX_PLAUSIBLE_WEIGHT_KG } from '../../../lib/units'

const ONE_DAY = 24 * 60 * 60 * 1000

function todayIso() {
  return new Date().toLocaleDateString('en-CA')
}

function yesterdayIso() {
  const d = new Date(Date.now() - ONE_DAY)
  return d.toLocaleDateString('en-CA')
}

export function WeightForm({ onSubmit, loading, lastEntry }) {
  const { t, i18n } = useTranslation()
  const { weightUnit, weightToKg, displayValueWeight } = useUnits()
  const today = todayIso()
  const yesterday = yesterdayIso()

  const [date, setDate] = useState(today)
  const [weight, setWeight] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [dateOpen, setDateOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)

  // Friendly date label for the pill — "Today" / "Yesterday" for the
  // common cases, otherwise a localized date.
  const dateLabel = useMemo(() => {
    if (date === today) return t('phoenixTracker.weight.dateToday')
    if (date === yesterday) return t('phoenixTracker.weight.dateYesterday')
    try {
      return new Date(`${date}T00:00:00`).toLocaleDateString(i18n.language || 'en', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return date
    }
  }, [date, today, yesterday, t, i18n.language])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!date || !weight || Number(weight) <= 0) return
    const kgValue = weightToKg(weight)
    if (!isPlausibleWeightKg(kgValue)) return
    setSubmitting(true)
    const success = await onSubmit(date, kgValue, note.trim() || null)
    if (success) {
      setWeight('')
      setNote('')
      setNoteOpen(false)
    }
    setSubmitting(false)
  }

  // Out-of-range weights are silently rejected by handleSubmit; mirror
  // that on the button so users see the disabled state instead of a
  // submit that does nothing.
  const enteredKg = weight ? weightToKg(weight) : null
  const outOfRange = enteredKg != null && !isPlausibleWeightKg(enteredKg)
  const isDisabled =
    loading || submitting || !weight || Number(weight) <= 0 || outOfRange

  return (
    <Card>
      <CardContent className="p-5">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date pill — collapsed by default. Tapping reveals the date input. */}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setDateOpen((o) => !o)}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              dir="auto"
            >
              <Calendar className="w-3.5 h-3.5" />
              <span>{dateLabel}</span>
              {dateOpen
                ? <ChevronUp className="w-3 h-3" />
                : <ChevronDown className="w-3 h-3" />}
            </button>
          </div>
          {dateOpen && (
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-11 w-full px-3 rounded-lg border border-input bg-background text-foreground font-mono text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
              dir="ltr"
              required
            />
          )}

          {/* Hero weight input. Big, centered, single focus point. The
              underline + ghost placeholder + caption together make it
              obvious that this is a tappable input, not decorative text —
              without sacrificing the Apple-Health-style minimalism. */}
          <div>
            <div className="flex items-baseline justify-center gap-2">
              <NumberInput
                inputMode="decimal"
                step="any"
                min="0"
                max="500"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={
                  lastEntry?.weight != null
                    ? displayValueWeight(Number(lastEntry.weight)).toFixed(1)
                    : t('phoenixTracker.weight.weightPlaceholder')
                }
                required
                dir="ltr"
                className="w-40 text-center text-5xl font-bold font-mono bg-transparent border-0 border-b-2 border-input focus:border-app-primary-500 outline-none focus:outline-none placeholder:text-muted-foreground/30 text-foreground tabular-nums transition-colors pb-1"
              />
              <span className="text-lg text-muted-foreground font-medium pb-2">
                {weightUnit}
              </span>
            </div>
            {!weight && (
              <p className="text-center text-[11px] text-muted-foreground mt-2" dir="auto">
                {t('phoenixTracker.weight.tapToEnter')}
              </p>
            )}
            {outOfRange && (
              <p className="text-center text-[11px] text-red-500 mt-2" dir="ltr">
                Max {MAX_PLAUSIBLE_WEIGHT_KG} kg
              </p>
            )}
          </div>

          {/* Optional note — collapsed by default. */}
          <div>
            <button
              type="button"
              onClick={() => setNoteOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="w-3 h-3" />
              {t('phoenixTracker.weight.addNote')}
            </button>
            {noteOpen && (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('phoenixTracker.weight.notePlaceholder')}
                rows={2}
                className="mt-2 w-full px-3 py-2 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-y"
              />
            )}
          </div>

          <Button
            type="submit"
            disabled={isDisabled}
            className="w-full h-11 bg-app-primary-500 hover:bg-app-primary-600 text-white text-sm font-medium"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin me-2" />
                {t('phoenixTracker.saving')}
              </>
            ) : (
              t('phoenixTracker.weight.logWeight')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
